/**
 * matrix.js — NexChat Matrix Client with E2EE
 *
 * Uses matrix-js-sdk v34 Rust crypto (initRustCrypto).
 * Signal-like model: E2EE on by default, silently, zero user-facing prompts.
 *
 * How the "just works" model works:
 *
 *  1. initRustCrypto() — Rust Olm/Megolm, stable IndexedDB per device ID.
 *     Completely separate from legacy crypto stores — no version conflicts.
 *
 *  2. After first sync PREPARED, bootstrapCryptoSilently():
 *     a. bootstrapSecretStorage  — 4S set up with a deterministically-derived
 *        key (no user password ever needed). Idempotent.
 *     b. bootstrapCrossSigning   — uploads cross-signing keys via OIDC token
 *        UIA (no password dialog). Idempotent.
 *     c. checkKeyBackupAndEnable — automatic Megolm key backup to server.
 *        This is what stops "Unable to decrypt" after logout/login.
 *     d. globalBlacklistUnverifiedDevices = false — send to all devices,
 *        verified or not. No blocks, no prompts.
 *
 *  3. Decryption failures trigger a silent key-request retry (2s delay).
 *     MatrixEventEvent.Decrypted fires when key arrives and re-emits the
 *     message through the normal pipeline.
 *
 *  4. createRoom() always sets m.megolm.v1.aes-sha2 encryption.
 *
 * Root causes fixed vs old codebase:
 *  ✓ "Unable to decrypt" walls — key backup + silent retry
 *  ✓ Verification dialogs      — trust-all model
 *  ✓ Crypto store version mismatch — Rust crypto has its own clean store
 *  ✓ Cross-signing UIA password prompt — OIDC token used for UIA
 *
 * CRITICAL: Never run two MatrixClient instances against the same IndexedDB.
 * Always call stopMatrix() before creating a new client.
 */

import * as sdk from 'matrix-js-sdk';

// Polyfill Buffer for matrix-js-sdk's encodeBase64 (uses Buffer.from().toString('base64'))
if (typeof window !== 'undefined' && typeof window.Buffer === 'undefined') {
  window.Buffer = {
    from: (data, encoding) => ({
      toString: (enc) => {
        if (enc === 'base64') {
          if (data instanceof Uint8Array) {
            return btoa(Array.from(data).map(b => String.fromCharCode(b)).join(''));
          }
          return btoa(String.fromCharCode(...new Uint8Array(data)));
        }
        return '';
      }
    })
  };
}

let _client      = null;
let _accessToken = null; // kept for UIA during cross-signing bootstrap

/* ──────────────────────────────────────────────
   Events dispatched to window
─────────────────────────────────────────────── */
export const EV = {
  READY:         'nx:ready',
  SYNC_STATE:    'nx:sync-state',
  ROOM_UPDATE:   'nx:room-update',
  ROOM_TIMELINE: 'nx:timeline',
  TYPING:        'nx:typing',
  READ_RECEIPT:  'nx:read-receipt',
  REACTION:      'nx:reaction',
  REDACTION:     'nx:redaction',
  PINS_UPDATED:  'nx:pins-updated',
  CRYPTO_READY:  'nx:crypto-ready',
  ROOM_TOPIC:    'nx:room-topic',
  STORY_UPDATED: 'nx:story-updated',
};

function emit(name, detail = {}) {
  window.dispatchEvent(new CustomEvent(name, { detail }));
}

/* ──────────────────────────────────────────────
   Deterministic key derivation
   Same device ID + user ID = same keys, always.
   No passwords, no recovery key prompts, ever.
─────────────────────────────────────────────── */
async function _derivePickleKey(deviceId, userId) {
  // Pickle key is device-specific (protects local crypto store)
  const data = new TextEncoder().encode(`nexchat|pickle|${deviceId}|${userId}`);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return new Uint8Array(hash);
}

async function _deriveSecretStorageKey(deviceId, userId) {
  // 4S key is user-specific (NOT device-specific) so all devices
  // for the same user can access the same key backup and cross-signing keys
  const data = new TextEncoder().encode(`nexchat|4s|${userId}`);
  const hash = await crypto.subtle.digest('SHA-256', data);
  // Must return Uint8Array (not ArrayBuffer) — SDK's encodeBase64 requires Uint8Array instanceof check
  return new Uint8Array(hash);
}

function _uint8ArrayToBase64(uint8Array) {
  // Browser-safe base64 encoding — polyfill for SDK's encodeBase64
  return btoa(uint8Array.reduce((acc, current) => acc + String.fromCharCode(current), ''));
}

/* ──────────────────────────────────────────────
   Init Matrix client with Rust E2EE
─────────────────────────────────────────────── */
export async function initMatrix({ homeserverUrl, accessToken, userId, deviceId }) {
  if (_client) await stopMatrix();

  _accessToken = accessToken;

  const pickleKey = await _derivePickleKey(deviceId, userId);

  _client = sdk.createClient({
    pendingEventOrdering: 'detached',
    baseUrl:     homeserverUrl,
    accessToken,
    userId,
    deviceId,

    // Timeline store — persists room history across reloads
    store: new sdk.IndexedDBStore({
      indexedDB:    window.indexedDB,
      localStorage: window.localStorage,
      dbName:       'nexchat-matrix-store',
    }),

    // pickleKey encrypts the Rust crypto IndexedDB at rest.
    // Same device always gets the same key — store persists correctly.
    pickleKey,

    // getSecretStorageKey is called when SDK needs to unlock 4S
    // (e.g. on a second device restoring cross-signing keys from backup).
    // Derived deterministically — zero user interaction.
    cryptoCallbacks: {
      getSecretStorageKey: async ({ keys }) => {
        const keyId   = Object.keys(keys)[0];
        const privKey = await _deriveSecretStorageKey(deviceId, userId);
        return [keyId, privKey];
      },
      cacheSecretStorageKey: (keyId, keyInfo, key) => {
        // Cache called after 4S key is created — store key in session
        console.info('[crypto] 4S key cached for keyId:', keyId);
      },
    },
  });

  await _client.store.startup();

  // Rust crypto: stable, isolated from any legacy store, no version conflicts.
  await _client.initRustCrypto({ useIndexedDB: true });

  // Trust-all: never block on unverified devices. Signal-like UX.
  const cryptoApi = _client.getCrypto();
  if (cryptoApi) {
    cryptoApi.globalBlacklistUnverifiedDevices = false;
    // Also disable error on unknown devices so messages send to all devices
    try {
      _client.setGlobalErrorOnUnknownDevices(false);
    } catch (_) {}
  }

  _bindEvents();

  await _client.startClient({
    initialSyncLimit: 30,
    lazyLoadMembers:  true,
    presence: 'online',
  });

  window._mxClient = _client; // debug
  window._mxcToHttp = mxcToHttp; // for mention avatars
  return _client;
}

/* ──────────────────────────────────────────────
   Silent E2EE bootstrap — runs once after first sync
─────────────────────────────────────────────── */
async function _bootstrapCryptoSilently() {
  const cryptoApi = _client?.getCrypto();
  if (!cryptoApi) return;

  const deviceId = _client.getDeviceId();
  const userId   = _client.getUserId();

  // Step 1: Secret storage (4S)
  try {
    // Check if 4S is already set up
    const has4S = await _client.secretStorage.hasKey();
    if (!has4S) {
      // First time: create new 4S with derived key
      await cryptoApi.bootstrapSecretStorage({
        createSecretStorageKey: async () => {
          const privKey = await _deriveSecretStorageKey(deviceId, userId);
          return { privateKey: privKey };
        },
        setupNewSecretStorage: true,
        setupNewKeyBackup: true,
      });
      console.info('[crypto] 4S created');
    } else {
      // Already set up — just bootstrap to ensure cross-signing keys are stored
      await cryptoApi.bootstrapSecretStorage({
        getKeyFromStorage: async () => {
          return await _deriveSecretStorageKey(deviceId, userId);
        },
      });
      console.info('[crypto] 4S already set up');
    }
  } catch (err) {
    console.warn('[crypto] 4S bootstrap non-fatal:', err.message);
  }

  // Step 2: Cross-signing
  // Publishes cross-signing public keys to the homeserver.
  // UIA authenticated via OIDC access token — no password dialog.
  // Idempotent — no-op if keys already published.
  try {
    await cryptoApi.bootstrapCrossSigning({
      authUploadDeviceSigningKeys: async (makeRequest) => {
        try {
          // MAS supports OIDC token UIA
          await makeRequest({ type: 'org.matrix.login.token', token: _accessToken });
        } catch (_) {
          try {
            // Fallback: some Synapse/MAS combos allow null for OIDC sessions
            await makeRequest(null);
          } catch (e) {
            console.warn('[crypto] Cross-signing UIA failed, skipping:', e.message);
          }
        }
      },
    });
  } catch (err) {
    console.warn('[crypto] Cross-signing bootstrap non-fatal:', err.message);
  }

  // Step 3: Key backup — enable + restore
  try {
    const backupInfo = await cryptoApi.checkKeyBackupAndEnable();
    console.info('[crypto] Key backup enabled:', !!backupInfo);

    // Step 4: Restore keys from backup
    try {
      const result = await cryptoApi.restoreKeyBackup({
        progressCallback: (progress) => {
          if (progress.stage === 'fetch') console.info('[crypto] Fetching backed up keys...');
        }
      });
      if (result) console.info('[crypto] Keys restored:', result.imported, 'of', result.total);
    } catch (err) {
      console.warn('[crypto] Key restore non-fatal:', err.message);
    }
  } catch (err) {
    console.warn('[crypto] Key backup non-fatal:', err.message);
  }

  emit(EV.CRYPTO_READY);
  console.info('[crypto] E2EE ready');
}

/* ──────────────────────────────────────────────
   Update access token after OIDC refresh
─────────────────────────────────────────────── */
export function updateAccessToken(newToken) {
  _accessToken = newToken;
  if (_client) _client.setAccessToken(newToken);
}

/* ──────────────────────────────────────────────
   Stop client
   NOTE: Does NOT clear crypto store on normal stop.
   Clearing would wipe Megolm keys → "Unable to decrypt" on next login.
   Only wipe explicitly via wipeCryptoStoreAndReset().
─────────────────────────────────────────────── */
export async function stopMatrix() {
  if (_client) {
    try { _client.stopClient(); } catch (_) {}
    _client = null;
  }
  _accessToken = null;
}

// Nuclear option: wipes all stored keys. User loses old encrypted messages.
// Only call on explicit "reset device" user action.
export async function wipeCryptoStoreAndReset() {
  if (_client) {
    try { _client.stopClient(); } catch (_) {}
    try { await _client.clearStores(); } catch (_) {}
    _client = null;
  }
  for (const name of ['nexchat-matrix-store', 'matrix-js-sdk:crypto']) {
    try { await indexedDB.deleteDatabase(name); } catch (_) {}
  }
}

/* ──────────────────────────────────────────────
   Client accessors
─────────────────────────────────────────────── */
export function getClient() {
  if (!_client) throw new Error('Matrix client not initialised');
  return _client;
}
export function getClientOrNull() { return _client; }

/* ──────────────────────────────────────────────
   Event bindings
─────────────────────────────────────────────── */
let _cryptoBootstrapped = false;

function _bindEvents() {
  const c = _client;

  c.on(sdk.ClientEvent.Sync, (state) => {
    emit(EV.SYNC_STATE, { state });

    if (state === 'PREPARED') {
      emit(EV.READY);
      if (!_cryptoBootstrapped) {
        _cryptoBootstrapped = true;
        _bootstrapCryptoSilently().catch(err =>
          console.warn('[crypto] Bootstrap error (non-fatal):', err)
        );
      }
    }
  });

  c.on(sdk.RoomEvent.Timeline, (event, room, toStartOfTimeline) => {
    if (toStartOfTimeline) return;
    if (!room) return;

    const type = event.getType();

    if (type === 'm.room.encrypted') {
      // Emit as placeholder immediately — don't track yet
      // MatrixEventEvent.Decrypted will fire when keys arrive
      emit(EV.ROOM_TIMELINE, { roomId: room.roomId, event, encrypted: true });
      emit(EV.ROOM_UPDATE,   { roomId: room.roomId });
      _scheduleDecryptRetry(event);
      return;
    }

    if (type === 'm.room.message' || type === 'org.matrix.msc3381.poll.start') {
      if (_trackEvent(event.getId(), event)) return;
      emit(EV.ROOM_TIMELINE, { roomId: room.roomId, event, encrypted: false });
      emit(EV.ROOM_UPDATE,   { roomId: room.roomId });
    }
    if (type === 'm.reaction') {
      emit(EV.ROOM_UPDATE, { roomId: room.roomId });
      emit(EV.REACTION,    { roomId: room.roomId, event });
    }
    if (type === 'org.matrix.msc3381.poll.response' || type === 'org.matrix.msc3381.poll.end') {
      emit(EV.ROOM_UPDATE, { roomId: room.roomId });
      emit(EV.REACTION,    { roomId: room.roomId, event }); // reuse reaction event to trigger re-render
    }
    if (type === 'm.room.redaction') {
      emit(EV.ROOM_UPDATE,   { roomId: room.roomId });
      emit(EV.REDACTION,     { roomId: room.roomId, redactedEventId: event.event.redacts });
    }
  });

  // Fires when a previously-encrypted event is successfully decrypted
  // Always emit this — it needs to UPDATE the existing placeholder in UI
  c.on(sdk.MatrixEventEvent.Decrypted, (event) => {
    if (event.isDecryptionFailure?.()) {
      _scheduleDecryptRetry(event);
      return;
    }
    const roomId = event.getRoomId();
    if (!roomId) return;
    const evType = event.getType();
    // Poll responses/ends should never render as message bubbles
    if (evType === 'org.matrix.msc3381.poll.response' || evType === 'org.matrix.msc3381.poll.end') {
      emit(EV.REACTION, { roomId, event });
      emit(EV.ROOM_UPDATE, { roomId });
      return;
    }
    // Mark as seen now that it's decrypted (plaintext ID)
    _trackEvent(event.getId(), event);
    emit(EV.ROOM_TIMELINE, { roomId, event, encrypted: false, decryptedUpdate: true });
    emit(EV.ROOM_UPDATE,   { roomId });
  });

  c.on(sdk.RoomEvent.Name,         (room) => emit(EV.ROOM_UPDATE,  { roomId: room.roomId }));
  c.on(sdk.RoomEvent.Receipt,      (_, room) => emit(EV.READ_RECEIPT, { roomId: room?.roomId }));
  c.on(sdk.RoomEvent.MyMembership, (room) => emit(EV.ROOM_UPDATE,  { roomId: room.roomId }));

  // Emoji status via room state events
  c.on(sdk.RoomStateEvent.Events, (event) => {
    if (event.getType() === 'im.vector.user_status') {
      const content = event.getContent();
      emit(EV.PRESENCE, {
        userId:    event.getStateKey(),
        emoji:     content?.emoji || '',
        statusMsg: content?.text  || '',
      });
    }
    if (event.getType() === 'm.room.topic') {
      emit(EV.ROOM_TOPIC, {
        roomId: event.getRoomId(),
        topic:  event.getContent()?.topic || '',
      });
    }
    if (event.getType() === 'nx.story') {
      emit(EV.STORY_UPDATED, { roomId: event.getRoomId() });
    }
  });

  // Presence events — listen on all users via client re-emitter
  c.on(sdk.UserEvent.Presence, (event, user) => {
    emit(EV.PRESENCE, { userId: user?.userId, statusMsg: user?.presenceStatusMsg || '' });
  });
  c.on(sdk.UserEvent.CurrentlyActive, (event, user) => {
    emit(EV.PRESENCE, { userId: user?.userId, statusMsg: user?.presenceStatusMsg || '' });
  });

  c.on(sdk.RoomMemberEvent.Typing, (event, member) => {
    const room = c.getRoom(member.roomId);
    if (!room) return;
    // Get ALL currently typing members from room state
    const typingMembers = room.getJoinedMembers().filter(m => m.typing);
    const users = typingMembers.map(m => m.userId);
    emit(EV.TYPING, { roomId: member.roomId, users });
  });
}

/* ──────────────────────────────────────────────
   Silent decryption retry
   When a Megolm session key is missing, the SDK sends key-share
   requests to other devices via to-device messages. We wait 2s for
   them to arrive, then retry decryption.
─────────────────────────────────────────────── */
const _retrySet = new Set();
const _seenEvents = new Set(); // deduplicate events from multiple listeners

function _emitTimelineEvent(roomId, event) {
  const id = event.getId();
  if (!id || _seenEvents.has(id)) return;
  _seenEvents.add(id);
  // Trim cache to prevent unbounded growth
  if (_seenEvents.size > 500) {
    const first = _seenEvents.values().next().value;
    _seenEvents.delete(first);
  }
  emit(EV.ROOM_TIMELINE, { roomId, event });
  emit(EV.ROOM_UPDATE,   { roomId });
}
const _seenEventIds = new Set();

function _trackEvent(eventId, event) {
  if (!eventId) return false;
  // Also check transaction ID to catch pending→confirmed duplicates
  const txnId = event?.getUnsigned()?.transaction_id || event?.getTxnId?.();
  if (_seenEventIds.has(eventId)) return true;
  if (txnId && _seenEventIds.has('txn:' + txnId)) return true;
  _seenEventIds.add(eventId);
  if (txnId) _seenEventIds.add('txn:' + txnId);
  if (_seenEventIds.size > 1000) {
    const first = _seenEventIds.values().next().value;
    _seenEventIds.delete(first);
  }
  return false;
}
function _scheduleDecryptRetry(event) {
  const id = event.getId();
  if (_retrySet.has(id)) return;
  _retrySet.add(id);
  setTimeout(async () => {
    try { await _client?.decryptEventIfNeeded(event); } catch (_) {}
    _retrySet.delete(id);
  }, 2000);
}

/* ──────────────────────────────────────────────
   Room helpers
─────────────────────────────────────────────── */
export function getRooms() {
  if (!_client) return [];
  return _client.getRooms()
    .filter(r => r.getMyMembership() === 'join')
    .filter(r => r.name !== 'NexChat Stories')
    .sort((a, b) => _lastTs(b) - _lastTs(a));
}

function _lastTs(room) {
  const events = room.getLiveTimeline().getEvents();
  return events.length
    ? events[events.length - 1].getTs()
    : (room.getLastActiveTimestamp() || 0);
}

export function getRoom(roomId)   { return _client?.getRoom(roomId) ?? null; }
export function getRoomName(room) { return room.name || room.roomId; }

export function isDmRoom(room) {
  if (room.getDMInviter?.()) return true;
  const members = room.getJoinedMembers();
  return members.length === 2 && members.some(m => m.userId !== _client.getUserId());
}

export function getDmPartner(room) {
  const myId = _client.getUserId();
  return room.getJoinedMembers().find(m => m.userId !== myId) || null;
}

export function isEncryptedRoom(room) {
  return room.hasEncryptionStateEvent?.() || false;
}

export function getUnreadCount(room) {
  return room.getUnreadNotificationCount('total') || 0;
}

/* ──────────────────────────────────────────────
   Timeline helpers
─────────────────────────────────────────────── */
export function getTimeline(room, limit = 50) {
  return room.getLiveTimeline().getEvents()
    .filter(e => {
      if (e.isRedacted?.()) return false;
      const t = e.getType();
      if (t === 'm.room.encrypted') return true;
      if (t === 'org.matrix.msc3381.poll.start') return true;
      if (t !== 'm.room.message') return false;
      // Only exclude thread replies that are fully decrypted
      // If still encrypted, keep in main timeline as placeholder
      try {
        if (!e.isDecryptionFailure?.() && e.getContent()?.msgtype) {
          const rel = e.getContent()?.['m.relates_to'];
          if (rel?.rel_type === 'm.thread') return false;
        }
      } catch (_) {}
      return true;
    })
    .slice(-limit);
}

export async function loadMoreHistory(room) {
  if (!_client) return;
  await _client.paginateEventTimeline(room.getLiveTimeline(), {
    backwards: true, limit: 30,
  });
}

export function formatMessage(event) {
  const content = event.getContent();
  const failed  = event.isDecryptionFailure?.() || false;
  const txnId   = event.getUnsigned()?.transaction_id || event.getTxnId?.() || null;

  let replyTo = null;
  const rel = content?.['m.relates_to'];
  if (!failed && rel?.['m.in_reply_to']) {
    const replyEventId = rel['m.in_reply_to'].event_id;
    const room = _client?.getRoom(event.getRoomId());
    if (room) {
      const orig = room.findEventById(replyEventId);
      if (orig) {
        replyTo = {
          eventId:  replyEventId,
          senderId: orig.getSender() || '',
          body:     orig.getContent().body || '',
        };
      }
    }
  }

  // Handle file messages
  const isFileMsg = !failed && ['m.image','m.video','m.audio','m.file'].includes(content.msgtype);
  // nx.post is handled as a special message type
  if (!failed && content.msgtype === 'nx.post') {
    return {
      id: event.getId(), txnId,
      type: 'nx.post',
      body: content['nx.post.title'] || content.body || '',
      formatted: null,
      senderId: event.getSender(), ts: event.getTs(),
      status: event.status, failed: false, replyTo: null,
      fileUrl: null, fileInfo: null, fileName: null,
      fileEncrypted: null, _event: event,
    };
  }

  // Poll events
  const eventType = event.getType();

  // Call events
  if (!failed && (eventType === 'm.room.message') &&
      (content?.msgtype === 'org.matrix.call.invite' || content?.msgtype === 'org.matrix.call.end')) {
    return {
      id: event.getId(), txnId,
      type: content.msgtype,
      body: content.body || '',
      formatted: null, senderId: event.getSender(), ts: event.getTs(),
      status: event.status, failed: false, replyTo: null,
      fileUrl: null, fileInfo: null, fileName: null,
      fileEncrypted: null, _event: event,
    };
  }

  if (!failed && eventType === 'org.matrix.msc3381.poll.start') {
    return {
      id: event.getId(), txnId,
      type: 'org.matrix.msc3381.poll.start',
      body: content?.['org.matrix.msc3381.poll']?.question?.['org.matrix.msc1767.text'] || 'Poll',
      formatted: null, senderId: event.getSender(), ts: event.getTs(),
      status: event.status, failed: false, replyTo: null,
      fileUrl: null, fileInfo: null, fileName: null,
      fileEncrypted: null, _event: event,
    };
  }

  let body = failed ? '' : (content.body || '');
  // Strip Matrix reply fallback prefix "> <sender> body\n\nactual message"
  // Always strip regardless of whether replyTo resolved - it's noise in our UI
  if (body.startsWith('> ')) {
    const parts = body.split(String.fromCharCode(10));
    const realIdx = parts.findIndex(p => !p.startsWith('>'));
    if (realIdx !== -1) body = parts.slice(realIdx).join(String.fromCharCode(10)).trim();
  }

  return {
    id: event.getId(), txnId,
    type:      failed ? 'nx.decrypt_failed' : (content.msgtype || 'm.text'),
    body, formatted: failed ? null : (content.formatted_body || null),
    senderId: event.getSender(), ts: event.getTs(),
    status: event.status, failed, replyTo,
    // File info
    // E2EE files use content.file.url, unencrypted use content.url
    fileUrl:     isFileMsg ? (content.file?.url || content.url || content.external_url || null) : null,
    fileInfo:    isFileMsg ? content.info : null,
    fileName:    isFileMsg ? content.body : null,
    fileEncrypted: isFileMsg ? (content.file || null) : null, // full E2EE key info
    _event:      event, // raw event for decryptAndBlobUrl
  };
}

/* ──────────────────────────────────────────────
   File upload + send
─────────────────────────────────────────────── */
// Avatar cache — userId → mxc URI or null
const _avatarCache = new Map();

export async function getUserAvatarUrl(userId) {
  if (_avatarCache.has(userId)) return _avatarCache.get(userId);
  try {
    const profile = await _client.getProfileInfo(userId);
    const url = profile?.avatar_url || null;
    _avatarCache.set(userId, url);
    return url;
  } catch (_) {
    _avatarCache.set(userId, null);
    return null;
  }
}

// Pre-warm avatar cache from SDK's cached member data (no network requests)
export function prewarmAvatarCache() {
  if (!_client) return;
  const rooms = _client.getRooms();
  for (const room of rooms) {
    const members = room.getJoinedMembers();
    for (const member of members) {
      if (!_avatarCache.has(member.userId)) {
        // Use SDK's cached member data — no network request
        const mxc = member.getMxcAvatarUrl() || null;
        _avatarCache.set(member.userId, mxc);
      }
    }
  }
}

export async function getRoomAvatarUrl(room) {
  if (!room) return null;
  const event = room.currentState.getStateEvents('m.room.avatar', '');
  const mxc = event?.getContent()?.url;
  return mxc || null;
}

export async function pinMessage(roomId, eventId) {
  if (!_client) throw new Error('Not connected');
  const room = _client.getRoom(roomId);
  if (!room) throw new Error('Room not found');
  const pinEvent = room.currentState.getStateEvents('m.room.pinned_events', '');
  const currentPins = pinEvent?.getContent()?.pinned || [];
  if (currentPins.includes(eventId)) return; // already pinned
  return _client.sendStateEvent(roomId, 'm.room.pinned_events', {
    pinned: [...currentPins, eventId],
  });
}

export async function unpinMessage(roomId, eventId) {
  if (!_client) throw new Error('Not connected');
  const room = _client.getRoom(roomId);
  if (!room) throw new Error('Room not found');
  const pinEvent = room.currentState.getStateEvents('m.room.pinned_events', '');
  const currentPins = pinEvent?.getContent()?.pinned || [];
  return _client.sendStateEvent(roomId, 'm.room.pinned_events', {
    pinned: currentPins.filter(id => id !== eventId),
  });
}

export function getPinnedEventIds(room) {
  if (!room) return [];
  const pinEvent = room.currentState.getStateEvents('m.room.pinned_events', '');
  return pinEvent?.getContent()?.pinned || [];
}

export async function updateDisplayName(name) {
  if (!_client) throw new Error('Not connected');
  return _client.setDisplayName(name);
}

export async function updateAvatar(mxcUri) {
  if (!_client) throw new Error('Not connected');
  return _client.setAvatarUrl(mxcUri);
}

export async function getMyProfile() {
  if (!_client) throw new Error('Not connected');
  const userId = _client.getUserId();
  try {
    const profile = await _client.getProfileInfo(userId);
    return { displayName: profile.displayname, avatarUrl: profile.avatar_url };
  } catch (_) { return {}; }
}

export function updateProfile() {} // stub for compat


/* ──────────────────────────────────────────────
   Emoji Status — stored as account data + room state
─────────────────────────────────────────────── */
export async function setEmojiStatus(emoji, text) {
  if (!_client) return;
  const myId = _client.getUserId();
  const status = (emoji || text) ? { emoji: emoji || '', text: text || '' } : {};

  // Set in ALL joined rooms as state event so other members can see it
  const rooms = _client.getRooms();
  const promises = rooms.map(room =>
    _client.sendStateEvent(room.roomId, 'im.vector.user_status', status, myId)
      .catch(() => {}) // ignore rooms where we lack permissions
  );
  await Promise.allSettled(promises);
}

export async function getEmojiStatusForUser(userId, roomId) {
  if (!_client) return null;
  try {
    // Read from room state — works for any room member
    const room = roomId ? _client.getRoom(roomId) : null;
    if (room) {
      const stateEvent = room.currentState.getStateEvents('im.vector.user_status', userId);
      if (stateEvent) {
        const content = stateEvent.getContent();
        if (content?.emoji || content?.text) return content;
      }
    }
    // Fallback: check any shared room
    const rooms = _client.getRooms();
    for (const r of rooms) {
      const members = r.getJoinedMembers().map(m => m.userId);
      if (members.includes(userId)) {
        const ev = r.currentState.getStateEvents('im.vector.user_status', userId);
        if (ev) {
          const content = ev.getContent();
          if (content?.emoji || content?.text) return content;
        }
      }
    }
    return null;
  } catch { return null; }
}


/* ──────────────────────────────────────────────
   Per-room Notification Settings
   Uses Matrix push rules API
─────────────────────────────────────────────── */
export async function getRoomNotifSetting(roomId) {
  if (!_client) return 'all';
  try {
    const rules = _client.getPushRules();
    // Check for room-specific override rules
    const overrideRules = rules?.global?.override || [];
    const roomRule = overrideRules.find(r =>
      r.conditions?.some(c => c.kind === 'event_match' && c.key === 'room_id' && c.pattern === roomId)
    );
    if (roomRule) {
      if (!roomRule.enabled) return 'all';
      const notify = roomRule.actions?.includes('notify') ||
        roomRule.actions?.some(a => a?.set_tweak === 'notify');
      return notify ? 'all' : 'mute';
    }
    // Check room rules
    const roomRules = rules?.global?.room || [];
    const specificRule = roomRules.find(r => r.rule_id === roomId);
    if (specificRule) {
      if (!specificRule.enabled) return 'all';
      const hasDontNotify = specificRule.actions?.includes('dont_notify') ||
        specificRule.actions?.every(a => a !== 'notify' && a?.set_tweak !== 'highlight');
      return hasDontNotify ? 'mentions' : 'all';
    }
    return 'all';
  } catch { return 'all'; }
}

export async function setRoomNotifSetting(roomId, setting) {
  // setting: 'all' | 'mentions' | 'mute'
  if (!_client) return;
  try {
    // Remove any existing room rules first
    try {
      await _client.deletePushRule('global', 'room', roomId);
    } catch (_) {}
    try {
      await _client.deletePushRule('global', 'override', roomId + '_mute');
    } catch (_) {}

    if (setting === 'mute') {
      // Add override rule that suppresses all notifications
      await _client.addPushRule('global', 'override', roomId + '_mute', {
        conditions: [{ kind: 'event_match', key: 'room_id', pattern: roomId }],
        actions: ['dont_notify'],
        enabled: true,
      });
    } else if (setting === 'mentions') {
      // Add room rule that only notifies on mentions
      await _client.addPushRule('global', 'room', roomId, {
        actions: ['dont_notify'],
        enabled: true,
      });
    }
    // 'all' = no rules = default behaviour
  } catch (err) {
    console.error('[pushRule] error:', err);
  }
}

export async function setPresence(presence, statusMsg) {
  if (!_client) return;
  try {
    const matrixPresence = { online: 'online', unavailable: 'unavailable', busy: 'unavailable', offline: 'offline' };
    // Store statusMsg as JSON so emoji can be recovered
    await _client.setSyncPresence(matrixPresence[presence] || 'online', statusMsg || undefined);
  } catch (_) {}
}

export function getUserPresenceStatus(userId) {
  if (!_client) return null;
  try {
    const user = _client.getUser(userId);
    return {
      presence: user?.presence || 'offline',
      statusMsg: user?.presenceStatusMsg || null,
      lastActive: user?.lastActiveAgo || null,
    };
  } catch { return null; }
}

export async function uploadFile(file) {
  if (!_client) throw new Error('Not connected');
  const response = await _client.uploadContent(file, {
    name: file.name,
    type: file.type,
    onlyContentUri: false,
  });
  // SDK v34 returns string URI directly or object with content_uri
  const uri = typeof response === 'string' ? response : (response?.content_uri || response);
  return { content_uri: uri };
}

// Convert mxc:// URI to https download URL using SDK (adds auth token)
export function searchMessages(query, roomId) {
  // Client-side search through decrypted timeline (works with E2EE)
  if (!_client || !query) return [];
  const q = query.toLowerCase();
  const room = roomId ? _client.getRoom(roomId) : null;
  const rooms = room ? [room] : _client.getRooms();
  const results = [];

  for (const r of rooms) {
    // Search both live timeline and all loaded timelines
    const events = r.getLiveTimeline().getEvents();
    for (const event of events) {
      if (event.getType() !== 'm.room.message') continue;
      if (event.isDecryptionFailure?.()) continue;
      const content = event.getContent();
      const body = content?.body || '';
      if (!body.toLowerCase().includes(q)) continue;
      const sender = event.getSender();
      const member = r.getMember(sender);
      results.push({
        eventId:   event.getId(),
        roomId:    r.roomId,
        roomName:  r.name || r.roomId,
        senderId:  sender,
        senderName: member?.name || sender.split(':')[0].slice(1),
        body,
        ts:        event.getTs(),
        msgtype:   content?.msgtype,
        encrypted: r.hasEncryptionStateEvent(),
      });
    }
  }

  return results.sort((a, b) => b.ts - a.ts).slice(0, 100);
}

export async function searchMessagesServer(query, roomId, matrixToken) {
  // Server-side search via Synapse (works for non-E2EE rooms, searches full history)
  if (!_client || !query || !matrixToken) return [];
  try {
    const searchBody = {
      search_categories: {
        room_events: {
          search_term: query,
          order_by: 'recent',
          event_context: { before_limit: 0, after_limit: 0, include_profile: true },
          ...(roomId ? { filter: { rooms: [roomId] } } : {}),
        }
      }
    };
    const res = await _client.search({ body: searchBody });
    const roomEvents = res?.search_categories?.room_events;
    return (roomEvents?.results || []).map(r => ({
      eventId:   r.result?.event_id,
      roomId:    r.result?.room_id,
      roomName:  _client.getRoom(r.result?.room_id)?.name || r.result?.room_id,
      senderId:  r.result?.sender,
      senderName: r.result?.sender?.split(':')[0].slice(1),
      body:      r.result?.content?.body || '',
      msgtype:   r.result?.content?.msgtype,
      ts:        r.result?.origin_server_ts,
      encrypted: false,
      rank:      r.rank,
    })).filter(r => r.body && r.eventId);
  } catch (e) {
    return [];
  }
}

export function mxcToHttp(mxc) {
  if (!mxc) return '';
  if (mxc.startsWith('http://') || mxc.startsWith('https://') || mxc.startsWith('/')) return mxc;
  const clean = mxc.replace('mxc://', '');
  const parts = clean.split('/');
  if (parts.length < 2) return '';
  // Always get fresh token - never cache it
  const token = _client?.getAccessToken() || localStorage.getItem('nx_access_token');
  const base = 'https://nexchat.ethosserver.com/_matrix/client/v1/media/download/'
    + parts[0] + '/' + parts[1];
  return token ? base + '?access_token=' + encodeURIComponent(token) : base;
}

// Dynamic version that always returns fresh URL (use for img src)
export function mxcToHttpFresh(mxc) {
  return mxcToHttp(mxc);
}

export async function decryptAndBlobUrl(event) {
  try {
    const content = event.getContent();
    const mimeType = content.info?.mimetype || 'image/jpeg';

    // External URL (GIF, etc.) — return directly, no fetch needed
    if (content.external_url && !content.external_url.startsWith('mxc://')) {
      return content.external_url;
    }

    // E2EE encrypted file — content.file has key/iv/hashes
    if (content.file?.url && content.file?.key) {
      const fileInfo = content.file;
      const mediaUrl = mxcToHttp(fileInfo.url); // already includes ?access_token=
      const res = await fetch(mediaUrl);
      if (!res.ok) throw new Error('fetch failed: ' + res.status);
      const cipherBuf = await res.arrayBuffer();

      // Decode JWK key (base64url -> CryptoKey)
      const keyData = fileInfo.key.k.replace(/-/g, '+').replace(/_/g, '/');
      const keyBytes = Uint8Array.from(atob(keyData), c => c.charCodeAt(0));
      const cryptoKey = await crypto.subtle.importKey(
        'raw', keyBytes, { name: 'AES-CTR' }, false, ['decrypt']
      );

      // Decode IV (base64url, 16 bytes for AES-CTR)
      const ivB64 = fileInfo.iv.replace(/-/g, '+').replace(/_/g, '/');
      const ivPadded = ivB64.padEnd(ivB64.length + (4 - ivB64.length % 4) % 4, '=');
      const iv = Uint8Array.from(atob(ivPadded), c => c.charCodeAt(0));

      // Decrypt AES-256-CTR
      const plainBuf = await crypto.subtle.decrypt(
        { name: 'AES-CTR', counter: iv, length: 64 },
        cryptoKey,
        cipherBuf
      );

      const blob = new Blob([plainBuf], { type: mimeType });
      return URL.createObjectURL(blob);
    }

    // Unencrypted mxc file — mxcToHttp already adds token
    if (content.url?.startsWith('mxc://')) {
      const url = mxcToHttp(content.url); // already includes ?access_token=
      const res = await fetch(url);
      if (!res.ok) throw new Error('fetch failed: ' + res.status);
      const blob = await res.blob();
      return URL.createObjectURL(new Blob([await blob.arrayBuffer()], { type: mimeType }));
    }

    return null;
  } catch (e) {
    console.warn('[media] decryptAndBlobUrl failed:', e.message);
    return null;
  }
}

export async function sendExternalMedia(roomId, url, mimetype) {
  if (!_client) throw new Error('Not connected');
  return _client.sendMessage(roomId, {
    msgtype: 'm.image',
    body: url.split('/').pop() || 'image',
    url: url,
    external_url: url,
    info: { mimetype },
  });
}


/* ──────────────────────────────────────────────
   Voice Messages
─────────────────────────────────────────────── */

/* ──────────────────────────────────────────────
   Polls (MSC3381)
─────────────────────────────────────────────── */

/* ──────────────────────────────────────────────
   Voice / Video Calls — Element Call
─────────────────────────────────────────────── */
const ELEMENT_CALL_URL = 'https://call.ethosserver.com';

export function getElementCallUrl(roomId, displayName, avatarUrl, isVideo = true) {
  const params = new URLSearchParams({
    embed:            'true',
    skipLobby:        'true',
    hideHeader:       'true',
    preload:          'true',
    allowIceFallback: 'true',
    viaServers:       'ethosserver.com',
  });

  if (displayName) params.set('displayName', displayName);
  if (avatarUrl)   params.set('avatarUrl', avatarUrl);
  if (!isVideo)    params.set('hideVideo', 'true');

  // Element Call URL format to join a specific existing Matrix room
  return `${ELEMENT_CALL_URL}/#/room/${encodeURIComponent(roomId)}?${params.toString()}`;
}

export async function sendCallInvite(roomId, isVideo = true) {
  // Send a call invite message so other members know a call is starting
  if (!_client) throw new Error('Not connected');
  const callType = isVideo ? 'video' : 'voice';
  return _client.sendEvent(roomId, 'm.room.message', {
    msgtype: 'org.matrix.call.invite',
    body: `📞 Started a ${callType} call`,
    call_type: callType,
    call_url: getElementCallUrl(roomId, '', null, isVideo),
  });
}

export async function sendCallEnd(roomId) {
  if (!_client) throw new Error('Not connected');
  return _client.sendEvent(roomId, 'm.room.message', {
    msgtype: 'org.matrix.call.end',
    body: '📞 Call ended',
  }).catch(() => {});
}

export function isCallInviteEvent(event) {
  return event.getType() === 'm.room.message' &&
    event.getContent()?.msgtype === 'org.matrix.call.invite';
}

export async function sendPoll(roomId, question, answers, kind = 'org.matrix.msc3381.poll.disclosed', maxSelections = 1) {
  if (!_client) throw new Error('Not connected');
  const answersWithIds = answers.map((text, i) => ({
    id: 'option_' + i + '_' + Date.now(),
    'org.matrix.msc1767.text': text,
  }));
  return _client.sendEvent(roomId, 'org.matrix.msc3381.poll.start', {
    'org.matrix.msc3381.poll': {
      question: { 'org.matrix.msc1767.text': question },
      kind,
      max_selections: maxSelections,
      answers: answersWithIds,
    },
    'org.matrix.msc1767.text': question,
  });
}

export async function sendPollVote(roomId, pollEventId, answerIds) {
  if (!_client) throw new Error('Not connected');
  return _client.sendEvent(roomId, 'org.matrix.msc3381.poll.response', {
    'm.relates_to': {
      rel_type: 'm.reference',
      event_id: pollEventId,
    },
    'org.matrix.msc3381.poll.response': {
      answers: answerIds,
    },
  });
}

export async function endPoll(roomId, pollEventId) {
  if (!_client) throw new Error('Not connected');
  return _client.sendEvent(roomId, 'org.matrix.msc3381.poll.end', {
    'm.relates_to': {
      rel_type: 'm.reference',
      event_id: pollEventId,
    },
    'org.matrix.msc1767.text': 'The poll has ended.',
  });
}

export function getPollResults(room, pollEventId, answers) {
  // Tally votes from poll.response events
  if (!_client || !room) return {};
  const myId = _client.getUserId();
  const votes = {}; // userId -> [answerId]
  let isClosed = false;

  for (const event of room.getLiveTimeline().getEvents()) {
    const type = event.getType();
    if (type === 'org.matrix.msc3381.poll.end') {
      const rel = event.getContent()?.['m.relates_to'];
      if (rel?.event_id === pollEventId) isClosed = true;
    }
    if (type !== 'org.matrix.msc3381.poll.response') continue;
    const rel = event.getContent()?.['m.relates_to'];
    if (!rel || rel.event_id !== pollEventId) continue;
    const response = event.getContent()?.['org.matrix.msc3381.poll.response'];
    if (!response) continue;
    // Last vote per user wins
    votes[event.getSender()] = response.answers || [];
  }

  // Tally
  const tally = {}; // answerId -> count
  for (const a of answers) tally[a.id] = 0;
  for (const [, answerIds] of Object.entries(votes)) {
    for (const id of answerIds) {
      if (tally[id] !== undefined) tally[id]++;
    }
  }

  const myVote = votes[myId] || [];
  const totalVotes = Object.values(votes).length;

  return { tally, myVote, totalVotes, isClosed };
}


/* ──────────────────────────────────────────────
   Message Threading (m.thread)
─────────────────────────────────────────────── */
export async function sendThreadReply(roomId, threadRootEventId, text) {
  if (!_client) throw new Error('Not connected');
  return _client.sendEvent(roomId, 'm.room.message', {
    msgtype: 'm.text',
    body: text,
    'm.relates_to': {
      rel_type: 'm.thread',
      event_id: threadRootEventId,
      is_falling_back: false,
    },
  });
}

export function getThreadReplies(room, threadRootEventId) {
  if (!_client || !room) return [];
  const seen = new Set();
  const allEvents = [];

  // 1. Live timeline
  for (const e of room.getLiveTimeline().getEvents()) {
    const id = e.getId();
    if (id && !seen.has(id)) { seen.add(id); allEvents.push(e); }
  }

  // 2. Thread timelines (SDK v34)
  try {
    const threads = room.getThreads?.() || [];
    for (const thread of threads) {
      const tl = thread.timeline || thread.liveTimeline?.getEvents?.() || [];
      for (const e of tl) {
        const id = e?.getId?.();
        if (id && !seen.has(id)) { seen.add(id); allEvents.push(e); }
      }
    }
  } catch (_) {}

  // 3. All room timelines
  try {
    for (const tl of room.getTimelines?.() || []) {
      for (const e of tl.getEvents?.() || []) {
        const id = e?.getId?.();
        if (id && !seen.has(id)) { seen.add(id); allEvents.push(e); }
      }
    }
  } catch (_) {}

  return allEvents.filter(e => {
    if (!e?.getType || e.getType() !== 'm.room.message') return false;
    if (e.isRedacted?.()) return false;
    const rel = e.getContent?.()?.['m.relates_to'];
    return rel?.rel_type === 'm.thread' && rel?.event_id === threadRootEventId;
  }).sort((a, b) => a.getTs() - b.getTs());
}

export function getThreadSummary(room, threadRootEventId) {
  // Returns { count, lastReply, participants }
  const replies = getThreadReplies(room, threadRootEventId);
  if (!replies.length) return null;
  const lastReply = replies[replies.length - 1];
  const participants = [...new Set(replies.map(e => e.getSender()))];
  return {
    count: replies.length,
    lastReplyBody: lastReply.getContent()?.body || '',
    lastReplySender: lastReply.getSender(),
    lastReplyTs: lastReply.getTs(),
    participants,
  };
}

export async function sendVoiceMessage(roomId, audioBlob, durationMs) {
  if (!_client) throw new Error('Not connected');

  // Upload audio blob as media
  const file = new File([audioBlob], 'voice-message.ogg', { type: audioBlob.type || 'audio/ogg' });
  const response = await _client.uploadContent(file, {
    name: 'voice-message.ogg',
    type: file.type,
    onlyContentUri: false,
  });
  const uri = typeof response === 'string' ? response : (response?.content_uri || response);

  // Send as m.audio with voice flag (MSC3245 — marks it as voice note not file)
  return _client.sendMessage(roomId, {
    msgtype: 'm.audio',
    body: 'Voice message',
    url: uri,
    info: {
      mimetype: file.type,
      size: audioBlob.size,
      duration: durationMs,
    },
    'org.matrix.msc3245.voice': {}, // marks as voice note
  });
}

export async function sendFile(roomId, file, mxcUri) {
  if (!_client) throw new Error('Not connected');

  const isImage = file.type.startsWith('image/');
  const isVideo = file.type.startsWith('video/');
  const isAudio = file.type.startsWith('audio/');

  let msgtype = 'm.file';
  if (isImage) msgtype = 'm.image';
  else if (isVideo) msgtype = 'm.video';
  else if (isAudio) msgtype = 'm.audio';

  const content = {
    msgtype,
    body: file.name,
    url: mxcUri,
    info: {
      mimetype: file.type,
      size: file.size,
    },
  };

  // Add dimensions for images
  if (isImage) {
    await new Promise(resolve => {
      const img = new Image();
      img.onload = () => {
        content.info.w = img.naturalWidth;
        content.info.h = img.naturalHeight;
        URL.revokeObjectURL(img.src);
        resolve();
      };
      img.onerror = resolve;
      img.src = URL.createObjectURL(file);
    });
  }

  return _client.sendMessage(roomId, content);
}

/* ──────────────────────────────────────────────
   Send message — E2EE is transparent to caller
─────────────────────────────────────────────── */
export async function sendMessage(roomId, text, replyTo = null) {
  if (!_client) throw new Error('Not connected');
  if (!text.trim()) return;

  const content = { msgtype: 'm.text', body: text.trim() };

  if (replyTo?.eventId) {
    content['m.relates_to'] = { 'm.in_reply_to': { event_id: replyTo.eventId } };
    const senderShort = (replyTo.senderId || '').split(':')[0].replace('@','');
    content.body = `> <${senderShort}> ${replyTo.body}\n\n${text.trim()}`;
    content.format = 'org.matrix.custom.html';
    content.formatted_body =
      `<mx-reply><blockquote><em>${senderShort}</em>: ` +
      `${_escHtmlMx(replyTo.body)}</blockquote></mx-reply>${_escHtmlMx(text.trim())}`;
  }

  return _client.sendMessage(roomId, content);
}

function _escHtmlMx(s) {
  return String(s||'').replace(/&/g,'&amp;').replace(/</g,'&lt;')
    .replace(/>/g,'&gt;').replace(/"/g,'&quot;');
}

/* ──────────────────────────────────────────────
   Typing notifications
─────────────────────────────────────────────── */
let _typingTimer = null;
export function sendTypingNotification(roomId, isTyping) {
  if (!_client) return;
  if (_typingTimer) clearTimeout(_typingTimer);
  _client.sendTyping(roomId, isTyping, 4000).catch(() => {});
  if (isTyping) {
    _typingTimer = setTimeout(() => {
      _client?.sendTyping(roomId, false, 0).catch(() => {});
    }, 4000);
  }
}

/* ──────────────────────────────────────────────
   Mark read
─────────────────────────────────────────────── */
export function markRoomAsRead(room) {
  if (!_client || !room) return;
  const events = room.getLiveTimeline().getEvents();
  // Only send receipt for confirmed server events (not pending local ones)
  // Pending events have IDs starting with ~ 
  const last = [...events].reverse().find(e => {
    const id = e.getId();
    return id && !id.startsWith('~') && e.getType() === 'm.room.message';
  });
  if (last) _client.sendReadReceipt(last).catch(() => {});
}


/* ──────────────────────────────────────────────
   Reactions
─────────────────────────────────────────────── */
export async function sendReaction(roomId, eventId, emoji) {
  if (!_client) throw new Error('Not connected');
  return _client.sendEvent(roomId, 'm.reaction', {
    'm.relates_to': {
      rel_type: 'm.annotation',
      event_id: eventId,
      key:      emoji,
    },
  });
}

export async function editMessage(roomId, eventId, newBody) {
  if (!_client) throw new Error('Not connected');
  return _client.sendEvent(roomId, 'm.room.message', {
    msgtype: 'm.text',
    body: '* ' + newBody,
    'm.new_content': { msgtype: 'm.text', body: newBody },
    'm.relates_to': { rel_type: 'm.replace', event_id: eventId },
  });
}

export async function redactEvent(roomId, eventId) {
  if (!_client) throw new Error('Not connected');
  return _client.redactEvent(roomId, eventId);
}

// Returns { emoji: [{ userId, eventId }] } for a given event
export function getReactionsForEvent(room, targetEventId) {
  if (!_client || !room) return {};
  const result = {};
  for (const event of room.getLiveTimeline().getEvents()) {
    if (event.getType() !== 'm.reaction') continue;
    const rel = event.getContent()?.['m.relates_to'];
    if (!rel || rel.rel_type !== 'm.annotation') continue;
    if (rel.event_id !== targetEventId) continue;
    const emoji = rel.key;
    if (!emoji) continue;
    if (!result[emoji]) result[emoji] = [];
    result[emoji].push({ userId: event.getSender(), eventId: event.getId() });
  }
  return result;
}
/* ──────────────────────────────────────────────
   Read receipt helpers
─────────────────────────────────────────────── */
export function debugReceipts(room) {
  if (!_client || !room) return {};
  const myId = _client.getUserId();
  const members = room.getJoinedMembers().filter(m => m.userId !== myId);
  const result = {};
  for (const member of members) {
    try {
      const receipt = room.getLastUnthreadedReceiptFor(member.userId);
      result[member.userId] = receipt;
    } catch (e) {
      result[member.userId] = 'ERROR: ' + e.message;
    }
  }
  return result;
}

export function getReadByList(room, eventTs) {
  // Returns list of {userId, displayName, avatarUrl, ts} who read up to this event
  if (!_client || !room) return [];
  const myId = _client.getUserId();
  const members = room.getJoinedMembers().filter(m => m.userId !== myId);
  const result = [];
  for (const member of members) {
    try {
      const receipt = room.getLastUnthreadedReceiptFor(member.userId);
      if (receipt && receipt.ts >= eventTs) {
        result.push({
          userId: member.userId,
          displayName: member.name || member.userId.split(':')[0].slice(1),
          avatarUrl: member.getMxcAvatarUrl() || null,
          ts: receipt.ts,
        });
      }
    } catch (_) {}
  }
  return result;
}

export function getRoomReadReceipts(room) {
  if (!_client || !room) return {};
  const myId = _client.getUserId();
  const result = {};
  const members = room.getJoinedMembers().filter(m => m.userId !== myId);
  const timeline = room.getLiveTimeline().getEvents();
  const eventIds = timeline.map(e => e.getId()).filter(id => id && !id.startsWith('~'));

  for (const member of members) {
    try {
      const receipt = room.getLastUnthreadedReceiptFor(member.userId);
      if (!receipt) continue;
      // receipt.eventId exists in newer SDK versions
      if (receipt.eventId && eventIds.includes(receipt.eventId)) {
        if (!result[receipt.eventId]) result[receipt.eventId] = [];
        result[receipt.eventId].push(member.userId);
        continue;
      }
      // Fallback: match by timestamp
      if (receipt.ts) {
        const event = [...timeline].reverse().find(e => {
          const id = e.getId();
          return id && !id.startsWith('~') && e.getTs() <= receipt.ts;
        });
        if (event) {
          const id = event.getId();
          if (!result[id]) result[id] = [];
          result[id].push(member.userId);
        }
      }
    } catch (_) {}
  }
  return result;
}


/* ──────────────────────────────────────────────
   Create room — E2EE enabled by default
─────────────────────────────────────────────── */

/* ──────────────────────────────────────────────
   Disappearing Messages
─────────────────────────────────────────────── */
export async function setDisappearingMessages(roomId, ttlMs) {
  // ttlMs = 0 means disabled. Uses m.room.retention with max_lifetime (ms).
  if (!_client) throw new Error('Not connected');
  // Write new event
  await _client.sendStateEvent(roomId, 'm.room.retention', {
    max_lifetime: ttlMs || null,
  }, '');
  // Clear old legacy event if turning off, so it can never be read as fallback
  if (!ttlMs) {
    try {
      await _client.sendStateEvent(roomId, 'm.room.message_retention', {}, '');
    } catch (_) {}
  }
}

export function getDisappearingMessageTtl(room) {
  if (!room) return 0;
  // Prefer m.room.retention (new). If it exists and is explicitly set, use it.
  const newEv = room.currentState.getStateEvents('m.room.retention', '');
  if (newEv) {
    const ttl = newEv.getContent()?.max_lifetime;
    return ttl ? Number(ttl) : 0; // null/0 means OFF
  }
  // Fall back to old event type only if new one doesn't exist at all
  const oldEv = room.currentState.getStateEvents('m.room.message_retention', '');
  if (oldEv) {
    const ttl = oldEv.getContent()?.nx_disappear || oldEv.getContent()?.max_lifetime;
    return ttl ? Number(ttl) : 0;
  }
  return 0;
}

// Redact expired messages — only messages sent AFTER the timer was enabled
export async function purgeExpiredMessages(roomId) {
  if (!_client) return;
  const room = _client.getRoom(roomId);
  if (!room) return;

  // Get the retention state event
  const retEv = room.currentState.getStateEvents('m.room.retention', '');
  if (!retEv) return;
  const ttl = retEv.getContent()?.max_lifetime;
  if (!ttl || ttl <= 0) return;

  // Only delete messages sent AFTER the retention event was set
  // This matches Signal/WhatsApp behavior — existing messages are never touched
  const timerEnabledAt = retEv.getTs?.() || 0;
  const now = Date.now();

  console.log(`[disappear] room ${roomId} ttl=${ttl}ms, timer enabled at ${new Date(timerEnabledAt).toISOString()}`);

  // Only process messages sent AFTER the timer was enabled
  // Messages that existed before timer was set are permanently immune
  const events = room.getLiveTimeline().getEvents();
  let count = 0;

  for (const event of events) {
    const type = event.getType();
    if (type !== 'm.room.message' && type !== 'm.room.encrypted') continue;
    if (event.isRedacted?.()) continue;

    const msgTs = event.getTs();

    // HARD RULE: never touch messages that predate the timer being enabled
    if (msgTs <= timerEnabledAt) continue;

    // Only delete if message age exceeds TTL
    const age = now - msgTs;
    if (age >= ttl) {
      console.log(`[disappear] redacting ${event.getId()} age=${Math.round(age/1000)}s`);
      try {
        await _client.redactEvent(roomId, event.getId());
        count++;
      } catch (e) {
        if (e.httpStatus === 404 || e.message?.includes('404') || e.message?.includes('Could not find room')) {
          console.warn('[disappear] room not found on server, skipping:', roomId);
          return;
        }
        console.warn('[disappear] redact failed:', e.message);
      }
    }
  }
  if (count > 0) console.log(`[disappear] purged ${count} expired messages in ${roomId}`);
}

/* ──────────────────────────────────────────────
   Delete Chat / Empty Room
─────────────────────────────────────────────── */
export async function emptyRoom(roomId) {
  // Admin: redact ALL messages — paginate full history
  if (!_client) throw new Error('Not connected');
  const room = _client.getRoom(roomId);
  if (!room) throw new Error('Room not found');

  // Paginate to load full timeline
  const timeline = room.getLiveTimeline();
  let hasMore = true;
  while (hasMore) {
    try {
      hasMore = await _client.paginateEventTimeline(timeline, { backwards: true, limit: 100 });
    } catch (_) { break; }
  }

  const events = timeline.getEvents()
    .filter(e => e.getType() === 'm.room.message' && !e.isRedacted?.());
  for (const event of events) {
    try { await _client.redactEvent(roomId, event.getId()); } catch (_) {}
  }
}

export async function clearMyMessages(roomId) {
  // Member: redact only own messages
  if (!_client) throw new Error('Not connected');
  const room = _client.getRoom(roomId);
  if (!room) throw new Error('Room not found');
  const myId = _client.getUserId();
  const events = room.getLiveTimeline().getEvents()
    .filter(e => e.getType() === 'm.room.message' && !e.isRedacted?.() && e.getSender() === myId);
  for (const event of events) {
    try { await _client.redactEvent(roomId, event.getId()); } catch (_) {}
  }
}

export async function kickUser(roomId, userId, reason = 'Removed by admin') {
  if (!_client) throw new Error('Not connected');
  return _client.kick(roomId, userId, reason);
}

export async function leaveRoom(roomId) {
  if (!_client) throw new Error('Not connected');
  return _client.leave(roomId);
}

export function isRoomAdmin(room) {
  if (!_client || !room) return false;
  const myId = _client.getUserId();
  const member = room.getMember(myId);
  return (member?.powerLevel ?? 0) >= 100;
}

export async function backfillDmRoomNames() {
  if (!_client) return;
  const rooms = _client.getRooms().filter(r => r.getMyMembership() === 'join');

  for (const room of rooms) {
    // Only process DM rooms
    const members = room.getJoinedMembers();
    const myId = _client.getUserId();
    if (members.length !== 2 || !members.some(m => m.userId !== myId)) continue;

    // Skip if room already has a name
    const existingName = room.currentState.getStateEvents('m.room.name', '')?.getContent()?.name;
    if (existingName && existingName.trim()) continue;

    // Get both display names
    try {
      const myDisplay = _client.getUser(myId)?.displayName || myId.split(':')[0].slice(1);
      const partner   = members.find(m => m.userId !== myId);
      let theirDisplay;
      try {
        const profile = await _client.getProfileInfo(partner.userId);
        theirDisplay  = profile?.displayname || partner.userId.split(':')[0].slice(1);
      } catch (_) {
        theirDisplay  = partner.name || partner.userId.split(':')[0].slice(1);
      }

      const names    = [myDisplay, theirDisplay].sort((a, b) => a.localeCompare(b));
      const roomName = names[0] + ' & ' + names[1];

      await _client.sendStateEvent(room.roomId, 'm.room.name', { name: roomName }, '');
      console.info('[dm] Named room', room.roomId, '->', roomName);
    } catch (err) {
      console.warn('[dm] Could not name room', room.roomId, err.message);
    }
  }
}

export async function createRoom(name, isDm = false, inviteUserIds = []) {
  if (!_client) throw new Error('Not connected');

  // For DMs, auto-name the room "DisplayA & DisplayB" (sorted for consistency)
  let roomName = isDm ? undefined : name;
  if (isDm && inviteUserIds.length > 0) {
    try {
      const myUserId   = _client.getUserId();
      const myDisplay  = _client.getUser(myUserId)?.displayName
                         || myUserId.split(':')[0].slice(1);

      // Fetch the other user's display name
      let theirDisplay = null;
      for (const uid of inviteUserIds) {
        try {
          const profile = await _client.getProfileInfo(uid);
          theirDisplay = profile?.displayname || uid.split(':')[0].slice(1);
        } catch (_) {
          theirDisplay = uid.split(':')[0].slice(1);
        }
        break; // only need the first invitee for DMs
      }

      // Sort alphabetically so "Alice & Bob" is the same regardless of who starts it
      const names = [myDisplay, theirDisplay].sort((a, b) => a.localeCompare(b));
      roomName = names[0] + ' & ' + names[1];
    } catch (_) {
      roomName = undefined; // fall back to unnamed if anything fails
    }
  }

  const { room_id } = await _client.createRoom({
    name:       roomName,
    preset:     isDm ? sdk.Preset.TrustedPrivateChat : sdk.Preset.PrivateChat,
    invite:     inviteUserIds,
    is_direct:  isDm,
    visibility: sdk.Visibility.Private,
    initial_state: [{
      type:      'm.room.encryption',
      state_key: '',
      content:   { algorithm: 'm.megolm.v1.aes-sha2' },
    }],
  });

  return room_id;
}

/* ──────────────────────────────────────────────
   User helpers
─────────────────────────────────────────────── */
export function buildUserId(localpart, domain) {
  return localpart.startsWith('@') ? localpart : `@${localpart}:${domain}`;
}

export async function searchUsers(term) {
  if (!_client) return [];
  try {
    return (await _client.searchUserDirectory({ term, limit: 10 })).results || [];
  } catch (_) { return []; }
}

export function getMyUserId() { return _client?.getUserId() || null; }

export function getMyDisplayName() {
  const uid = _client?.getUserId();
  if (!uid) return '';
  return _client.getUser(uid)?.displayName || uid.split(':')[0].slice(1);
}

export function getDisplayName(userId) {
  if (!_client) return userId;
  return _client.getUser(userId)?.displayName || userId.split(':')[0].slice(1);
}

export function getHomeserverDomain() {
  return _client?.getUserId()?.split(':')[1] || '';
}

export async function fetchUserProfile(accessToken, homeserverUrl) {
  const res = await fetch(`${homeserverUrl}/_matrix/client/v3/account/whoami`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) throw new Error(`whoami failed: ${res.status}`);
  return res.json();
}


export async function sendPost(roomId, title, body) {
  if (!_client) throw new Error('Not connected');
  return _client.sendMessage(roomId, {
    msgtype: 'nx.post',
    body: title + '\n\n' + body, // fallback for clients that don't support nx.post
    'nx.post.title': title,
    'nx.post.body': body,
    format: 'org.matrix.custom.html',
    formatted_body: '<strong>' + title + '</strong><br>' + body,
  });
}

export async function setRoomTopic(roomId, topic) {
  if (!_client) throw new Error('Not connected');
  return _client.sendStateEvent(roomId, 'm.room.topic', { topic }, '');
}

export function getRoomTopic(room) {
  if (!room) return '';
  const ev = room.currentState.getStateEvents('m.room.topic', '');
  return ev?.getContent()?.topic || '';
}

/* ──────────────────────────────────────────────
   Stories
─────────────────────────────────────────────── */
const STORIES_ROOM_ALIAS = '#nx-stories:ethosserver.com';
const STORIES_ROOM_ID = '!nueALqECrSFXHUTljE:ethosserver.com';
const STORY_TTL = 24 * 60 * 60 * 1000; // 24 hours

export async function getOrCreateStoriesRoom() {
  if (!_client) return null;

  // Check already-joined rooms first
  const joined = _client.getRooms();
  const existing = joined.find(r => r.roomId === STORIES_ROOM_ID || r.name === 'NexChat Stories');
  if (existing) return existing;

  // Force join known room ID
  try {
    await _client.joinRoom(STORIES_ROOM_ID);
    await new Promise(r => setTimeout(r, 2000));
    const room = _client.getRoom(STORIES_ROOM_ID);
    if (room) return room;
  } catch (e) {
    console.warn('[stories] join failed:', e.message);
  }

  return null;
}

export async function postStory(roomId, story) {
  // story = { text, imageUrl, color, bgColor }
  if (!_client) return;
  const content = {
    text: story.text || '',
    imageUrl: story.imageUrl || null,
    color: story.color || '#ffffff',
    bgColor: story.bgColor || '#6c3ef4',
    ts: Date.now(),
    expires: Date.now() + STORY_TTL,
  };
  return _client.sendStateEvent(roomId, 'nx.story', content, _client.getUserId());
}

export function getActiveStories(room) {
  if (!_client || !room) return [];
  const now = Date.now();
  const stateEvents = room.currentState.getStateEvents('nx.story');
  const stories = [];
  for (const ev of stateEvents) {
    const content = ev.getContent();
    if (!content?.ts) continue;
    if (content.expires && content.expires < now) continue; // expired
    const userId = ev.getStateKey();
    const member = room.getMember(userId);
    stories.push({
      userId,
      displayName: member?.name || userId.split(':')[0].slice(1),
      avatarUrl: member?.getMxcAvatarUrl() || null,
      text: content.text || '',
      imageUrl: content.imageUrl || null,
      color: content.color || '#ffffff',
      bgColor: content.bgColor || '#6c3ef4',
      ts: content.ts,
      expires: content.expires,
      isOwn: userId === _client.getUserId(),
    });
  }
  return stories.sort((a, b) => b.ts - a.ts);
}

export function getStoriesRoomId() {
  if (!_client) return null;
  try {
    // Try to find already-joined stories room
    const rooms = _client.getRooms();
    const sr = rooms.find(r => r.currentState.getStateEvents('m.room.aliases')?.getContent()?.aliases?.includes(STORIES_ROOM_ALIAS)
      || r.name === 'NexChat Stories');
    return sr?.roomId || null;
  } catch { return null; }
}
